/**
 * useArtifact — single-artifact fetcher.
 *
 * Wraps `GET /api/v4/artifacts/<id>` for any caller that needs to render or
 * inspect one artifact. Used by:
 *
 * - `FocusedArtifactPage` (`/artifact/<id>` standalone route).
 * - V4 routes (Categories / Accounts / Pipeline) when `?selectedArtifact=<id>` is
 *   present and the artifact may live outside the current list scope. The
 *   independent fetch is what lets the detail panel render before — or
 *   without — the list returning.
 * - `ChatDrawer` for hydrating an artifact when the drawer opens from a
 *   non-list surface.
 *
 * Design choices:
 *
 * - Single canonical query key `['artifact', id]` so all callers share one
 *   cache entry. Mounting the hook twice with the same id never double-fetches.
 * - 404 / 403 are returned as `{ data: null, notFound: true }` rather than
 *   thrown. Callers consume this via `useSelectedScopeGuard`, which classifies
 *   it as the "access-denied" rung of the cross-scope ladder; a typed null is
 *   more useful than an error object for that branching.
 * - All other non-OK responses throw — react-query surfaces them as
 *   `error` and the caller decides how to render.
 * - `staleTime: Infinity` matches `useViewArtifacts` — SSE controls freshness.
 * - The response body is parsed through `FocusedArtifactSchema` via
 *   `fetchJson`, keeping us inside the Validate-At-Trust-Boundaries rule. A
 *   payload that fails validation logs a warning and throws (`malformed`).
 */

import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../lib/api';
import { ApiProblemError, fetchJson } from '../../lib/apiFetchJson';
import {
  FocusedArtifactSchema,
  type FocusedArtifact,
  type ArtifactAdminContext,
} from '../../../../../src/shared/view-system/view-item.schema';

export type { FocusedArtifact as ArtifactResponse, ArtifactAdminContext };

type FetchOutcome =
  | { kind: 'ok'; artifact: FocusedArtifact }
  | { kind: 'notFound' };

export const artifactQueryKey = (id: string) => ['artifact', id] as const;

interface UseArtifactOptions {
  /** When false the query stays disabled regardless of `id`. Defaults to true. */
  enabled?: boolean;
}

/**
 * Returns the artifact and its loading/error state.
 *
 * - `data` is the parsed `FocusedArtifact` on success, `null` while loading
 *   or after a 403/404.
 * - `notFound` is `true` after a 403/404 — the artifact either doesn't exist
 *   or the acting user doesn't have access. Callers should treat both the
 *   same way (strip the URL param).
 * - `adminContext` is populated when an admin is viewing another user's
 *   artifact (server attaches `_adminContext` to the response).
 */
export function useArtifact(id: string | null | undefined, opts?: UseArtifactOptions) {
  const { apiFetch } = useApi();
  const enabled = (opts?.enabled ?? true) && !!id;

  const query = useQuery<FetchOutcome>({
    queryKey: artifactQueryKey(id ?? ''),
    queryFn: async () => {
      let parsed: FocusedArtifact;
      try {
        // Cast narrows the schema-inferred type (z.string() artifactCard/intent)
        // to the registry-narrowed `FocusedArtifact` type. Same pattern as
        // useViewArtifacts.ts. fetchJson parses at runtime (a drifted payload
        // throws `malformed`) even though TS sees the narrow type.
        parsed = (await fetchJson(
          apiFetch,
          FocusedArtifactSchema,
          `/api/v4/artifacts/${encodeURIComponent(id!)}`,
        )) as FocusedArtifact;
      } catch (err) {
        if (err instanceof ApiProblemError && (err.status === 404 || err.status === 403)) {
          return { kind: 'notFound' as const };
        }
        throw err;
      }
      return { kind: 'ok' as const, artifact: parsed };
    },
    enabled,
    // SSE-driven invalidation, matching useViewArtifacts. No numeric TTL.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const outcome = query.data;
  const data = outcome?.kind === 'ok' ? outcome.artifact : null;
  const notFound = outcome?.kind === 'notFound';

  return {
    data,
    adminContext: data?._adminContext ?? null,
    notFound,
    isLoading: query.isLoading && enabled,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}
